import { Component, createRef } from 'react'
import { lengthByLocaleFormatter, weightByLocaleFormatter } from '~/utils/formatters'
import Input from '~/components/OrdersPage/Input'
import Measurements from '~/utils/Measurements'
import Search from '~/components/Search/Search'
import styles from '~/styles/ordersPage.orderItems.module.css'

export default class OrderItemsTable extends Component {
	static getInitialProps() {
		return {
			namespacesRequired: [ 'Orders', 'common' ],
		}
	}

	public constructor(props) {
		super(props)
		this.state = {}
		this.packagesItems = this.props.order.multiPackages?.packages_items
		if (Object.keys(this.props.order?.orderItems || {}).length && !this.props.orderItems) {
			this.state.orderItems = this.props.cloneTheObject(this.props.order.orderItems)
			Object.keys(this.state.orderItems).forEach(lineNumber => {
				const line = this.state.orderItems[lineNumber]
				const data = { weight: ((+(line['Item Weight']) || 1) / 1000), locale: 'en', separate: true }
				const { lb, oz } = weightByLocaleFormatter(data)
				this.state[lineNumber + '_lbValue'] = lb
				this.state[lineNumber + '_ozValue'] = oz
			})
		}
		this.measurements = new Measurements()
		this.measurements.init(this.props.order, this.props.authDealerId)
		this._maxTableWidth = 1800 + 'px'
	}

	public render(): JSX.Element | null {
		let tableStyle = {
			maxWidth: this._maxTableWidth
		}
		if (this.props.minTableWidth) {
			tableStyle.minWidth = this.props.minTableWidth
		} else if (this.props.mainWidth) {
			if (this.props.isEditing) {
				if (this.props.formWidth) {
					tableStyle.minWidth = 'calc(' + this.props.formWidth + 'px - 2em)'
				}
			}
		}
		return (
			<table
				className={(this.props.isEditing ? styles.isEditing : null)}
				style={tableStyle}
			>
				{this._getTableContent()}
			</table>
		)
	}

	private _getTableContent(): JSX.Element {
		this._setHeaders()
		const { trs, hasBarcode } = this._getBody()
		if (hasBarcode) {
			const { trs: trsWithBarcode } = this._getBody(hasBarcode)
			trs = trsWithBarcode
		}
		return (
			<>
				<thead
					onClick={this._onTableHead.bind(this)}
				>
					<tr
						key="headers"
					>
						{this._getHeaders(hasBarcode)}
					</tr>
				</thead>
				<tbody>
					{trs}
				</tbody>
				{this._getMultiPackages()}
			</>
		)
	}

	private _getMultiPackages(hasBarcode): JSX.Element {
		const jsx = []
		const names = this.props.order.multiPackages?.package_nums
		if (names?.length) {
			this.props.order.multiPackages?.package_nums?.forEach((name, i) => {
				const isLastPackage = (names.length === i + 1)
				let className = styles.originToPackagesLine
				if (isLastPackage) {
					className += " " + styles.isLastPackage
				}
				jsx.push((
					<thead>
						<tr 
							key={"emptyHeaders" + name}
							className={styles.tableDivider}
						>
							<th className={className}>
								<div/>
								<div/>
							</th>
							<th className={styles.tableName} colSpan="10">
								<div>
									{"Package " + name}
								</div>
							</th>
						</tr>
						<tr key={"headers" + name}>
							{this._getHeaders(hasBarcode, name, isLastPackage)}
						</tr>
					</thead>
				))
				const { trs } = this._getBody(hasBarcode, this.packagesItems[i], isLastPackage)
				jsx.push((
					<tbody>
						{trs}
					</tbody>
				))
			})
		}
		return jsx
	}

	private _setHeaders(): void {
		let headers = []
		const firstLineNumber = Object.keys(this.props?.order?.orderItems || {})[0]
		if (firstLineNumber) {
			if (this.packagesItems?.length) {
				headers.push("_pkg_")
			}
			Object.keys(this.props?.order?.orderItems?.[firstLineNumber])?.forEach(key => {
				if (key.substring(0, 1) !== "_") {
					const isLWH = [ 'length', 'width', 'height' ].includes(key)
					if (!(this.props.isEditing && isLWH)) {
						let noVat = false
						if (key === 'vat') {
							noVat = !(this.props.order.orderItems[firstLineNumber]?.['_showVat'])
						}
						if (!noVat) {
							headers.push(key)
						}
					}
				}
			})
		}
		this._headers = headers
	}

	private _getHeaders(hasBarcode, packageName, isLastPackage): Array<JSX.Element> {
		let jsx = []
		let headers = this._headers
		if (this.props.isEditing) {
			headers.push('')
		}
		this._headers.forEach(header => {
			if ((hasBarcode || header !== 'barcode')) {
				let className = styles[header.replace(/ /g, '')] || null
				if (header === '_pkg_') {
					className = (className || "") + " " + styles.isPackageHeader
					if (!packageName) {
						className = (className || "") + " " + styles.originToPackagesStart
					}
				} else if (packageName) {
					className = (className || "") + " " + styles.isPackageHeaders
				}
				jsx.push((
					<th
						key={header}
						className={className}
					>
						{this._getHeaderContent(header, packageName, isLastPackage)}
					</th>
				))
			}
		})
		return jsx
	}

	private _getHeaderContent(header, packageName, isLastPackage) {
		let content = this.props.t('common:' + header)
		if (header === 'weight') {
			let content = this.props.t('common:Item Weight')
		    if (this.measurements.weightMu === 'kg') {
		    	content = this.props.t('common:Weight, g')
		    }
		} else if (header === '_pkg_') {
			if (packageName) {
				if (isLastPackage) {
					content = null
				} else {
					let className = styles.originToPackagesLine
					content = (
						<div className={className}>
							<div/>
						</div>
					)
				}
			} else {
				content = (<div/>)
			}
		}
		return content
	}

	private _onTableHead(): void {
		if (!this.props.isAbleForEditing) {
			this.props.toHideTheOrder()
		}
	}

	private _getWeightView(value, mu) {
		if (!mu) {
			mu = this.props.order?.dealer_weight_mu
		}
		let weightView = Math.round(value)
		if (mu === 'lb') {
			weightView = weightByLocaleFormatter({ weight: (value / 1000), locale: 'en' })
		}
		return weightView
	}

	private _getDimensionView(value, mu) {
		if (!mu) {
			mu = this.props.order?.dealer_dimensions_mu
		}
		let dimensionView = Math.round(+value * 100) / 100
		if (mu === 'in') {
			dimensionView = lengthByLocaleFormatter({ length: +value, locale: 'en' })
		}
		return dimensionView
	}

	private _getBody(hasBarcode = false, packageItems, isLastPackage): Array<JSX.Element> {
		let charAmount = 0
		let trs = []
		if (!this.props.isNewOrder) {
			let orderItems = JSON.parse(JSON.stringify(this.props?.order?.orderItems || {}))
			if (typeof packageItems !== "undefined") {
				orderItems = {}
				packageItems?.forEach((item, i) => {
					item.isPackageItem = true
					item.length = item.dimensions[0]
					item.width = item.dimensions[1]
					item.height = item.dimensions[2]
					item.sku = item.sku || undefined
					orderItems[i] = item
				})
			}
			Object.keys(orderItems).forEach((line, i) => {
				let rowCharAmount = 0
				let tr = []
				if (!orderItems[line]?._isDefautItem) {
					this._headers.forEach(header => {
						if ((hasBarcode || header !== 'barcode')) {
							if (orderItems[line].hasOwnProperty(header)) {
								const originValue = orderItems[line][header]
								if (originValue) {
									rowCharAmount += (originValue + '').length
								}
								let value = originValue
								if (header === 'weight') {
									if (orderItems[line].isPackageItem) {
										value = this.measurements.muToString(value, this.props.t, true)
									} else {
										value = this._getWeightView(value, this.measurements.weightMu)
									}
								}
								if ([ 'length', 'width', 'height' ].includes(header)) {
									if (orderItems[line].isPackageItem) {
										value = this.measurements.muToString(value, this.props.t)
									} else {
										value = this._getDimensionView(value, this.measurements.dimensionalMu)
									}
								}
								if (header === 'vat') {
									if (value === '-1') {
										value = ''
									}
								}
								if (header === 'total_price') {
									const total = (orderItems[line]?.['price'] || 0) 
										* (orderItems[line]?.['quantity'] || 0)
									value = (Math.round(total * 100) / 100).toFixed(2)
								}
								let valueComponent = value
								if (this.props.isEditing) {
									if (header === 'sku') {
										valueComponent = (
											<input
												type="text"
												className={styles.sku}
												value={value || ''}
												onChange={this._onChangeSku.bind(this, line)}
											/>
										)
									} else if (header === 'weight') {
										if (this.props.order?.dealer_weight_mu !== 'lb') {
											valueComponent = (
												<input
													type="number"
													min="1"
													max="25000"
													step="1"
													value={value}
													className={styles.weight}
													onFocus={this._onInputFocus.bind(this)}
													onChange={this._onChangeWeight.bind(this, line)}
												/>
											)
										} else {
											this['weightLb' + line] = createRef()
											this['weightOz' + line] = createRef()
											const data = { weight: (originValue / 1000), locale: 'en', separate: true }
											let { lb, oz } = weightByLocaleFormatter(data)
											valueComponent = (
												<div className={styles.weightUs}>
													<Input
														value={lb}
														step="1"
														min="0"
														placeholder="0"
														onChange={this._onChangeWeightUs.bind(this, line)}
														after=" lb"
														widthRem="4"
														focused="true"
														t={this.props.t}
														setRef={this['weightLb' + line]}
													/>
													<Input
														value={oz}
														step="1"
														minToLb="0"
														maxToLb="15"
														placeholder="1"
														onChange={this._onChangeWeightUs.bind(this, line)}
														after=" oz"
														widthRem="4"
														marginLeftRem="0.5"
														focused="true"
														t={this.props.t}
														setRef={this['weightOz' + line]}
													/>
												</div>
											)
										}
									} else if (header === 'quantity') {
										valueComponent = (
											<input
												type="number"
												min="1"
												step="1"
												value={value}
												onFocus={this._onInputFocus.bind(this)}
												onChange={this._onChangeQuantity.bind(this, line)}
											/>
										)
									} else if (header === 'price') {
										valueComponent = (
											<input
												type="number"
												min="0"
												step="0.01"
												value={value}
												className={styles.price}
												onFocus={this._onInputFocus.bind(this)}
												onChange={this._onChangePrice.bind(this, line)}
											/>
										)
									}
								}
								tr.push((
									<td
										key={header + line}
										className={styles[header.replace(/ /g, '')] || null}
									>
										{valueComponent}
									</td>
								))
								if (header === 'barcode' && value) {
									hasBarcode = true
								}
							} else {
								if (!i) {
									if (header === '_pkg_') {
										const itemsCount = Object.keys(this.props.order.orderItems).length
										let className = styles.originToPackagesLine
										if (isLastPackage) {
											className += " " + styles.isLastPackageItems
										} else if (typeof packageItems === "undefined") {
											className += " " + styles.isOriginItems
										}
										tr.push((
											<td
												key={header + line}
												rowSpan={itemsCount}
												className={className}
											>
												<div/>
											</td>
										))
									}
								}
							}
						}
					})
					if (this.props.isEditing) {
						tr.push((
							<td
								key={'delete' + line}
								className={styles.deleteTheLineTd}
							>
								<div
									className={styles.deleteTheLine}
									onClick={this._deleteTheLine.bind(this, line)}
								>
									<span/>
								</div>
							</td>
						))
					}
					trs.push((
						<tr
							key={line}
							className={!i && this.packagesItems?.length ? styles.isOriginForPackages : null}
						>
							{tr}
						</tr>
					))
				}
				if (rowCharAmount > charAmount) {
					charAmount = rowCharAmount
				}
			})
		}
		if (this.props.isEditing) {
			trs.push((
				<tr
					key='add'
					className={styles.add}
				>
					<td
						colSpan="3"
						key={'addNewLine'}
					>
						<div
							className={styles.addAnItemFrame}
						>
							<Search
								placeholder={this.props.t('Orders:Add an Item')}
								onFound={this._onNewItemFound.bind(this)}
								onValue={this._onSearchValue.bind(this)}
								onHeight={this._onSearchExpand.bind(this)}
								setCallback={this._setSearchCallback.bind(this)}
								doAction={this._onAddNewLine.bind(this)}
								keys={Object.keys(this.props.allDealerOrdersItems || {})}
							/>
							<div
								className={styles.addNewLineFrame}
							>
								<div
									className={styles.addNewLine + (this.props.newItem ? '' : ' ' + styles.disabled)}
									onClick={this._onAddNewLine.bind(this)}
								>
									<span/>
								</div>
							</div>
						</div>
					</td>
				</tr>
			))
		}
		return { trs, hasBarcode, charAmount }
	}

	private _onChangeSku(line: number, event): void {
		this._onChange('sku', event.target.value, line, true)
	}

	private _onInputFocus(event): void {
		event.target.select()
	}

	private _onChangeWeight(line: number, event): void {
		this._onChange('weight', event.target.value, line)
	}
	
	private _onChangeWeightLb(line: number, event): void {
		const lbValue = event.target.value
		const lbKey = line + '_lbValue'
		this.props.updateState({ [lbKey]: lbValue })
		const ozValue = +(this['weightOz' + line]?.current?.value)
		this._onChangeWeightUs(line, lbValue, ozValue)
	}
	
	private _onChangeWeightOz(line: number, event): void {
		const lbValue = +(this['weightLb' + line]?.current?.value)
		const ozValue = event.target.value
		const ozKey = line + '_ozValue'
		this.props.updateState({ [ozKey]: ozValue })
		this._onChangeWeightUs(line, lbValue, ozValue)
	}
	
	private _onChangeWeightUs(line: number, event): void {
		const lbWeight = +(this['weightLb' + line]?.current?.value) || 0
		const ozWeight = +(this['weightOz' + line]?.current?.value) || 0
		const grWeight = ((+lbWeight + (ozWeight / 16)) * 1000) / 2.20462262185
		this._onChange('weight', grWeight, line)
	}

	private _onChangeQuantity(line, event): void {
		let value = event.target.value
		if (value <= 0) {
			value = 1
		}
		this._onChange('quantity', value, line)
	}

	private _onChangePrice(line, event): void {
		this._onChange('price', event.target.value, line, true)
	}

	private _onChange(key, value, line, isMaybeZero): void {
		if (value || (isMaybeZero && +value === 0)) {
			let orderItems = this.props.cloneTheObject(this.props.orderItems)
				if ([ 'price', 'quantity', 'weight' ].includes(key)) {
					value = Math.abs(value)
				}
				orderItems[line][key] = value
				let newTotal = 0
				if (key === 'quantity') {
					newTotal = value * this.props.orderItems[line]['price']
				} else if (key === 'price') {
					newTotal = value * this.props.orderItems[line]['quantity']
				}
				if (newTotal || (isMaybeZero && +value === 0)) {
					orderItems[line]['total_price'] = (Math.round(newTotal * 100) / 100).toFixed(2)
				}
			this.props.updateState({ orderItems })
			if (!this._onChangeSTs) {
				this._onChangeSTs = {}
			}
			if (this._onChangeSTs[key]) {
				clearTimeout(this._onChangeSTs[key])
			}
			this._onChangeSTs[key] = setTimeout(() => {
				let order = this.props.cloneTheObject(this.props.order)
					order.orderItems[line][key] = value
					const { grTotalWeight, totalPrice } = this._getOrderItemsSummary(order)
					order.in_weight = grTotalWeight / 1000
					let muWeight = order.in_weight
					if (this.props.order?.dealer_weight_mu === 'lb') {
						muWeight = order.in_weight * 2.20462262185
					}
					order.mu_in_weight = muWeight
					order.in_total = totalPrice
				this.props.onNewOrder(order, true)
			}, 300)
		}
	}

	private _deleteTheLine(line: number): void {
		let order = this.props.cloneTheObject(this.props.order)
		let itemsCount = Object.keys(order.orderItems).length
		if (itemsCount !== 1) {
			delete order.orderItems[line]
		}
		order = this._toLineOrderItems(order)
		if (itemsCount === 1) {
			this.props.setIsNewOrder?.(true)
			order.isNew = true
			order.hasDefaultItem = true
			order.orderItems[1]['isDefautItem'] = true
		}
		this.props.updateState({ orderItems: (itemsCount === 1 ? null : order?.orderItems) })
		const { grTotalWeight, totalPrice } = this._getOrderItemsSummary(order)
		order.in_weight = grTotalWeight / 1000
		let muWeight = order.in_weight
		if (this.props.order?.dealer_weight_mu === 'lb') {
			muWeight = order.in_weight * 2.20462262185
		}
		order.mu_in_weight = muWeight
		order.in_total = totalPrice
		this.props.onNewOrder(order, true)
	}

	private _toLineOrderItems(order) {
		if (order?.orderItems) {
			let linedOrderItems = {}
			Object.keys(order.orderItems).forEach((lineNumber, i) => {
				const newLineNumber = (i + 1)
				const line = order.orderItems[lineNumber]
				line['line_num'] = newLineNumber
				linedOrderItems[newLineNumber] = line
			})
			order.orderItems = linedOrderItems
		}
		return order
	}

	private _getOrderItemsSummary(order) {
		const lines = Object.keys(order?.orderItems || {})
		let itemsAmount = 0
		let totalPrice = 0
		let grTotalWeight = 0
		lines.forEach(lineNumber => {
			const line = order.orderItems[lineNumber]
			itemsAmount += (+line['quantity'] || 0)
			if (!order?.isNew) {
				totalPrice += +(parseFloat((+line['price'] || 0) * (+line['quantity'] || 0)).toFixed(2))
				grTotalWeight += (+line['weight'] || 0) * (+line['quantity'] || 0)
			}
		})
		totalPrice = (Math.round(totalPrice * 100) / 100).toFixed(2)
		return { grTotalWeight, totalPrice }
	}

	private _onSearchValue(value): void {
		this.props.updateState({ searchValue: value })
	}
	
	private _onNewItemFound(key): void {
		let newItem = {}
		if (this.props.allDealerOrdersItems?.[key]) {
			newItem = this.props.allDealerOrdersItems?.[key]
		}
		newItem['title'] = key
		this.props.updateState({ newItem })
	}
	
	private _onSearchExpand(maxBottom: number): void {
		const mainRect = this.props.mainRef.current?.getBoundingClientRect()
		if (maxBottom > mainRect.bottom) {
			this.props.updateState({ mainHeight: mainRect.height + (maxBottom - mainRect.bottom) })
		}
	}

	private _setSearchCallback(callback, remove): void {
		if (!remove) {
			this._searchCallback = callback
		} else {
			this._searchCallback = null
		}
	}

	private _onAddNewLine(): void {
		if (this.props?.newItem) {
			let order = this.props.cloneTheObject(this.props.order)
			if (order.isNew) {
				delete order.isNew
			}
			let itemsAmount = Object.keys(order?.orderItems || {}).length
			if (this.props.order?.hasDefaultItem) {
				itemsAmount -= 1
			}
			let lineNumber = itemsAmount + 1
			if (this.props.isNewOrder) {
				lineNumber = 1
			}
			let newLine = this.props.cloneTheObject(this.props.newItem)
			if (!this.props.newItem['title']) {
				newLine['title'] = this.props.searchValue
				const weightGr = order.defaultPackageWeightGr
				const weightUs = order.defaultPackageWeightGr * (2.20462262185 / 1000)
				const weightLb = Math.trunc(weightUs)
				const weightOz = Math.round((weightUs % 1) * 16)
				const weightUsGr = (weightLb + weightOz / 16) / (2.20462262185 / 1000)
				newLine['weight'] = (this.props.order?.dealer_weight_mu !== 'lb' ? weightGr : weightUsGr)
			}
			newLine['quantity'] = 1
			if (!newLine['price']) {
				newLine['price'] = 1
			}
			newLine['total_price'] = (Math.round(newLine['price'] * 100) / 100).toFixed(2)
			newLine['line_num'] = lineNumber
			newLine['vat'] = ''
			newLine['sku'] = ''
			if (!newLine['length']) {
				newLine['length'] = order.defaultPackageLengthMm
			}
			if (!newLine['width']) {
				newLine['width'] = order.defaultPackageWidthMm
			}
			if (!newLine['height']) {
				newLine['height'] = order.defaultPackageHeightMm
			}
			
			const data = { weight: ((+(newLine['weight']) || 1) / 1000), locale: 'en', separate: true }
			const { lb, oz } = weightByLocaleFormatter(data)
			this.props.updateState({
				[lineNumber + '_lbValue']: lb,
				[lineNumber + '_ozValue']: oz,
			})
			
			let orderedNewLine = {}
			const firstItemNumber = Object.keys(order.orderItems).sort()[0]
			Object.keys(order.orderItems[firstItemNumber] || {}).forEach(key => {
				if (key !== 'isDefautItem') {
					orderedNewLine[key] = newLine[key]
				}
			})
			if (this.props.isNewOrder) {
				order = {}
			}
			if (this.props.isNewOrder) {
				this.props.updateState({ isNotNewOrder: true })
				this.props?.setIsNewOrder(false)
			}
			let orderItems = order.orderItems || {}
			if (order?.hasDefaultItem) {
				delete order.orderItems[1]
				order.hasDefaultItem = false
				orderItems = {}
			}
			orderItems[lineNumber] = orderedNewLine
			order.orderItems = orderItems
			order = this._toLineOrderItems(order)
			this.props.updateState({ orderItems: order?.orderItems || {}, newItem: null })
			const { grTotalWeight, totalPrice } = this._getOrderItemsSummary(order)
			order.in_weight = grTotalWeight / 1000
			let muWeight = order.in_weight
			if (this.props.order?.dealer_weight_mu === 'lb') {
				muWeight = order.in_weight * 2.20462262185
			}
			order.mu_in_weight = muWeight
			order.in_total = totalPrice
			
			this.props.onNewOrder(order, true)
			this._onDone()
		}
	}
	
	private _onDone(): void {
		if (this._searchCallback) {
			this._searchCallback()
		}
	}
}